"use client";

import { useCallback, useEffect, useState, type KeyboardEvent } from "react";

import { resolveAddressSuggestionListKey } from "./address-suggestion-list.keyboard";

export function useAddressSuggestionListKeyboard<T>(options: {
  enabled: boolean;
  items: T[];
  onSelect: (item: T, index: number) => void;
}): {
  highlightIndex: number;
  setHighlightIndex: (index: number) => void;
  onKeyDown: (event: KeyboardEvent<HTMLInputElement>) => void;
  isOpen: boolean;
} {
  const { enabled, items, onSelect } = options;
  const [dismissed, setDismissed] = useState(false);
  const [highlightIndex, setHighlightIndex] = useState(-1);

  useEffect(() => {
    setDismissed(false);
    setHighlightIndex(-1);
  }, [items]);

  const isOpen = enabled && !dismissed && items.length > 0;

  const onKeyDown = useCallback(
    (event: KeyboardEvent<HTMLInputElement>) => {
      const action = resolveAddressSuggestionListKey({
        isOpen,
        key: event.key,
        highlightIndex,
        itemCount: items.length,
      });
      if (action.type === "none") {
        return;
      }

      event.preventDefault();
      event.stopPropagation();

      if (action.type === "highlight") {
        setHighlightIndex(action.index);
        return;
      }
      if (action.type === "select") {
        const item = items[action.index];
        if (item !== undefined) {
          onSelect(item, action.index);
        }
        return;
      }
      setDismissed(true);
    },
    [highlightIndex, isOpen, items, onSelect],
  );

  return { highlightIndex, setHighlightIndex, onKeyDown, isOpen };
}
